import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface HorarioDisponivel {
  id: string;
  user_id: string;
  dia_semana: number;
  hora: string;
  hora_fim?: string;
  duracao_minutos?: number;
  tipo?: "expediente" | "horario";
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export const useHorariosDisponiveis = () => {
  const queryClient = useQueryClient();
  const activeClinicaId = useActiveClinicaId();

  const { data: horarios = [], isLoading } = useQuery({
    queryKey: ["horarios-disponiveis", activeClinicaId],
    queryFn: async () => {
      const data = await apiList<HorarioDisponivel>("horarios_disponiveis");
      return data.sort((a, b) => a.dia_semana - b.dia_semana || String(a.hora).localeCompare(String(b.hora)));
    },
  });

  const createHorario = useMutation({
    mutationFn: async (horario: { dia_semana: number; hora: string; hora_fim?: string; duracao_minutos?: number; tipo?: "expediente" | "horario"; ativo: boolean }) => {
      return apiCreate<HorarioDisponivel>("horarios_disponiveis", horario);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["horarios-disponiveis"] });
      toast({
        title: "Horário criado",
        description: "O horário foi adicionado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar horário",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateHorario = useMutation({
    mutationFn: async ({ id, ...horario }: Partial<HorarioDisponivel> & { id: string }) => {
      return apiUpdate<HorarioDisponivel>("horarios_disponiveis", id, horario);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["horarios-disponiveis"] });
      toast({
        title: "Horário atualizado",
        description: "O horário foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar horário",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteHorario = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("horarios_disponiveis", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["horarios-disponiveis"] });
      toast({
        title: "Horário excluído",
        description: "O horário foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir horário",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    horarios,
    isLoading,
    createHorario: createHorario.mutate,
    updateHorario: updateHorario.mutate,
    deleteHorario: deleteHorario.mutate,
  };
};
